import { useFormContext } from 'react-hook-form';
import type { GoToTest } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useGoToTest } from '../api';
import { useGoToTestColumns, useGridSelection } from '../hooks';
import { BaseGrid } from './BaseGrid';
import { SuspenseBoundary } from 'components/common';
import { useNavigate } from 'react-router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useListParams } from 'hooks';
import { debounce } from 'lodash-es';
import { usePermissionGuard } from 'features/login';

const GoToTestGridComponent = memo(() => {
  const { accessToChangeGototest } = usePermissionGuard();
  const pagination = usePagination([], 50);
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const { setQ, setApps, q: urlQ, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    q: string | undefined;
    apps: string[] | undefined;
  }>({
    q: urlQ ?? undefined,
    apps: (urlApps as string[]) ?? undefined,
  });

  const filterByQ = watch('q') as string;
  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (q: string, apps?: string[]) => {
      setDebouncedFilters({ q, apps: apps ?? undefined });
      setQ(q ?? undefined);
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setQ, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByQ, filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByQ, filterByApps, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedGoToTest');

  const columns = useGoToTestColumns();
  const { desc } = useListParams();

  const {
    data: { data: flows, count },
    isLoading,
  } = useGoToTest({
    params: {
      ...(debouncedFilters.q && { name: debouncedFilters.q }),
      ...(debouncedFilters.apps && { apps: debouncedFilters.apps }),
      desc,
      order_by: 'flow_name',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeGototest && navigate(`/cabinet/facebook/go-to-test/${id}`);
    },
    [navigate, accessToChangeGototest],
  );

  return (
    <BaseGrid<GoToTest>
      rows={flows}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as GoToTest).id);
      }}
    />
  );
});

GoToTestGridComponent.displayName = 'GoToTestGridComponent';

const GoToTestGrid = () => {
  return (
    <SuspenseBoundary>
      <GoToTestGridComponent />
    </SuspenseBoundary>
  );
};

export default GoToTestGrid;
